import { ActionType, ResourceType, useActionGuard } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { getMessageList } from '@lib/utils/getMessageList';
import useStores from '@stores/useStores';

export const canCreateTrackingPlan = () => {
  const { canPerformAction } = useActionGuard();

  const editTrackingPlanPermission = canEditTrackingPlan();

  const createTrackingPlanPermission = canPerformAction({
    old: { resourceType: 'workspace', action: 'edit' },
    new: {
      actionType: ActionType.CREATE,
      resource: {
        type: ResourceType.TRACKING_PLAN,
      },
    },
  });

  return {
    isAllowed: createTrackingPlanPermission.isAllowed && editTrackingPlanPermission.isAllowed,
    denialReason: getMessageList([
      createTrackingPlanPermission.denialReason,
      editTrackingPlanPermission.denialReason,
    ]),
  };
};

export const canEditTrackingPlan = (id?: string) => {
  const { canPerformAction } = useActionGuard();
  return canPerformAction({
    old: { resourceType: 'workspace', action: 'edit' },
    new: {
      actionType: ActionType.EDIT,
      resource: {
        id,
        type: ResourceType.TRACKING_PLAN,
      },
    },
  });
};

export const canDeleteTrackingPlan = (id: string) => {
  const { canPerformAction } = useActionGuard();
  const { trackingPlanListStore } = useStores();

  const trackingPlan = trackingPlanListStore.getById(id);
  if (!trackingPlan) {
    return {
      isAllowed: false,
      denialReason: 'Tracking plan not found',
    };
  }

  const hasConnectedSources = trackingPlan.connections.length > 0;
  if (hasConnectedSources) {
    return {
      isAllowed: false,
      denialReason: 'You cannot delete a tracking plan that is connected to sources',
    };
  }

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'edit' },
    new: {
      actionType: ActionType.DELETE,
      resource: {
        id,
        type: ResourceType.TRACKING_PLAN,
      },
    },
  });
};

export const canCopyTrackingPlan = () => {
  const { userStore, permissionsStore } = useStores();
  const { isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const hasPermission = isNewPermissionModelEnforcementEnabled
    ? userStore.isOrgAdmin
    : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' });

  return constructPermissionResult(
    hasPermission,
    'You do not have permission to copy tracking plans.',
  );
};

export const canConnectNewTrackingPlanToSource = (sourceId: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'source', action: 'edit', resourceId: sourceId },
    new: {
      actionType: ActionType.CONNECT_NEW,
      resources: [
        {
          type: ResourceType.EVENT_STREAM,
          id: sourceId,
        },
        { type: ResourceType.TRACKING_PLAN },
      ],
    },
  });
};

export const canConnectSourceToExistingTrackingPlans = (sourceId: string) => {
  const { canPerformAction } = useActionGuard();
  const { trackingPlanListStore } = useStores();
  return canPerformAction({
    old: { resourceType: 'source', action: 'edit', resourceId: sourceId },
    new: {
      actionType: ActionType.CONNECT_EXISTING,
      resources: [
        {
          type: ResourceType.EVENT_STREAM,
          id: sourceId,
        },
        { type: ResourceType.TRACKING_PLAN, id: trackingPlanListStore.plans.map((tp) => tp.id) },
      ],
    },
  });
};

export const canConnectTrackingPlanToExistingSources = (trackingPlanId: string) => {
  const { canPerformAction } = useActionGuard();
  const { sourcesListStore } = useStores();

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'edit' },
    new: {
      actionType: ActionType.CONNECT_EXISTING,
      resources: [
        {
          type: ResourceType.TRACKING_PLAN,
          id: trackingPlanId,
        },
        { type: ResourceType.EVENT_STREAM, id: sourcesListStore.sources.map((src) => src.id) },
      ],
    },
  });
};

export const canConnectTrackingPlanToSource = (trackingPlanId: string, sourceId: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'source', action: 'edit', resourceId: sourceId },
    new: {
      actionType: ActionType.CONNECT,
      resources: [
        {
          type: ResourceType.EVENT_STREAM,
          id: sourceId,
        },
        { type: ResourceType.TRACKING_PLAN, id: trackingPlanId },
      ],
    },
  });
};

export const canDisconnectTrackingPlanFromSource = (trackingPlanId: string, sourceId: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'source', action: 'edit', resourceId: sourceId },
    new: {
      actionType: ActionType.DISCONNECT,
      resources: [
        {
          type: ResourceType.EVENT_STREAM,
          id: sourceId,
        },
        { type: ResourceType.TRACKING_PLAN, id: trackingPlanId },
      ],
    },
  });
};

export const canViewTrackingPlanViolationSamples = (trackingPlanId: string) => {
  const { canPerformAction } = useActionGuard();

  return canPerformAction({
    old: { resourceType: 'workspace', action: 'pii' },
    new: {
      actionType: ActionType.PII_FAILURE_SAMPLES,
      resource: {
        type: ResourceType.TRACKING_PLAN,
        id: trackingPlanId,
      },
    },
  });
};
